import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './Privacy.css';

const LAST_UPDATED = 'May 2026';

const SECTIONS = [
  {
    id: 'who-we-are',
    num: '01',
    title: 'Who we are',
    summary: 'Studio, location, what this policy covers',
  },
  {
    id: 'information-we-collect',
    num: '02',
    title: 'Information we collect',
    summary: 'What you give us, and what we don\u2019t pick up automatically',
  },
  {
    id: 'how-we-use',
    num: '03',
    title: 'How we use your information',
    summary: 'Replies, quotes, and project communications',
  },
  {
    id: 'third-party-services',
    num: '04',
    title: 'Third-party services',
    summary: 'Formspree (forms) and PayPal (payments)',
  },
  {
    id: 'data-retention',
    num: '05',
    title: 'Data retention',
    summary: 'How long we keep things, and how to ask us to delete',
  },
  {
    id: 'your-rights',
    num: '06',
    title: 'Your rights',
    summary: 'Access, correction, deletion, withdrawal of consent',
  },
  {
    id: 'changes',
    num: '07',
    title: 'Changes to this policy',
    summary: 'How updates work and what continued use means',
  },
];

/* -----------------------------------------------------------------
   SCROLL PROGRESS
   ----------------------------------------------------------------- */
function ScrollProgress() {
  const [p, setP] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      setP(max > 0 ? Math.min(1, h.scrollTop / max) : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="pv-progress" aria-hidden="true">
      <div className="pv-progress-bar" style={{ transform: `scaleX(${p})` }} />
    </div>
  );
}

/* -----------------------------------------------------------------
   MARQUEE
   ----------------------------------------------------------------- */
function MarqueeStrip() {
  const items = [
    'Privacy policy',
    'Last updated · May 2026',
    'Layer & Co.',
    'Independent web studio',
    'Brooklyn-built',
    'No analytics on this site',
    'No tracking pixels',
    'Plain language, no dark patterns',
  ];

  const row = (k) => (
    <div className="pv-marquee-row" key={k}>
      {items.map((t, i) => (
        <span className="pv-marquee-item" key={i}>
          <span className="pv-marquee-dot" aria-hidden="true" />
          {t}
        </span>
      ))}
    </div>
  );

  return (
    <div className="pv-marquee" aria-hidden="true">
      <div className="pv-marquee-track">{row('a')}{row('b')}</div>
    </div>
  );
}

/* -----------------------------------------------------------------
   SCROLL-SPY TABLE OF CONTENTS
   ----------------------------------------------------------------- */
function TableOfContents({ activeId, onJump }) {
  return (
    <aside className="pv-toc" aria-label="On this page">
      <div className="pv-toc-corner" aria-hidden="true">
        <span /><span /><span /><span />
      </div>
      <p className="pv-toc-label">
        <span className="pv-toc-label-dot" />
        On this page
      </p>
      <ol className="pv-toc-list">
        {SECTIONS.map((s) => (
          <li
            key={s.id}
            className={`pv-toc-item ${activeId === s.id ? 'is-active' : ''}`}
          >
            <a
              href={`#${s.id}`}
              onClick={(e) => { e.preventDefault(); onJump(s.id); }}
            >
              <span className="pv-toc-num">{s.num}</span>
              <span className="pv-toc-title">{s.title}</span>
              <span className="pv-toc-mark" aria-hidden="true" />
            </a>
          </li>
        ))}
      </ol>
      <div className="pv-toc-foot">
        <span className="pv-toc-foot-k">Updated</span>
        <span className="pv-toc-foot-v">{LAST_UPDATED}</span>
      </div>
    </aside>
  );
}

/* -----------------------------------------------------------------
   PRIVACY PAGE
   ----------------------------------------------------------------- */
function Privacy() {
  const [activeId, setActiveId] = useState(SECTIONS[0].id);
  const sectionRefs = useRef({});
  const contentRef = useRef(null);

  const registerRef = useCallback((id) => (el) => {
    if (el) sectionRefs.current[id] = el;
  }, []);

  // Scroll-spy via IntersectionObserver
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: '-25% 0px -65% 0px', threshold: 0 }
    );
    Object.values(sectionRefs.current).forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, []);

  // Reveal-on-scroll
  useEffect(() => {
    const els = contentRef.current?.querySelectorAll('.pv-block') || [];
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-visible');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const jumpTo = useCallback((id) => {
    const el = sectionRefs.current[id];
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - 80;
    window.scrollTo({ top: y, behavior: 'smooth' });
  }, []);

  return (
    <main className="pv-page">
      <ScrollProgress />

      {/* -------- HEADER -------- */}
      <section className="pv-header">
        <div className="pv-header-noise" aria-hidden="true" />
        <div className="pv-container">
          <div className="pv-header-row">
            <div className="pv-header-main">
              <p className="pv-eyebrow">
                <span className="pv-eyebrow-rule" />
                Legal
              </p>
              <h1>
                Privacy <em>Policy</em>
                <span className="pv-h1-cursor" aria-hidden="true" />
              </h1>
              <p className="pv-lede">
                Plain-language, no surprises. Here&rsquo;s what we collect,
                how we use it, and what you can ask us to do about it.
              </p>
            </div>
            <div className="pv-header-stack">
              <div className="pv-header-stack-corner" aria-hidden="true">
                <span /><span /><span /><span />
              </div>
              <div className="pv-header-stack-row">
                <span>Document</span>
                <span>Privacy policy</span>
              </div>
              <div className="pv-header-stack-row">
                <span>Have a question?</span>
                <span>
                  <Link to="/contact" style={{ borderBottom: '1px solid currentColor', paddingBottom: '2px' }}>
                    Get in touch ↗
                  </Link>
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <MarqueeStrip />

      {/* -------- CONTENT -------- */}
      <section className="pv-content">
        <div className="pv-content-grid pv-container">
          <TableOfContents activeId={activeId} onJump={jumpTo} />

          <div className="pv-articles" ref={contentRef}>

            {/* 01 */}
            <article
              id="who-we-are"
              ref={registerRef('who-we-are')}
              className="pv-block"
            >
              <span className="pv-block-num" aria-hidden="true">01</span>
              <header className="pv-block-head">
                <p className="pv-block-tag">Section · 01</p>
                <h2>Who we are</h2>
              </header>
              <div className="pv-prose">
                <p>
                  Layer &amp; Co. is a web design studio based in New York. We build websites
                  for service businesses, practices, and small teams. This policy explains
                  what information we collect, how we use it, and your rights regarding that
                  information.
                </p>
                <p>
                  For questions about this policy, contact us through the{' '}
                  <Link to="/contact" className="pv-link">contact form</Link>.
                </p>
              </div>
            </article>

            {/* 02 */}
            <article
              id="information-we-collect"
              ref={registerRef('information-we-collect')}
              className="pv-block"
            >
              <span className="pv-block-num" aria-hidden="true">02</span>
              <header className="pv-block-head">
                <p className="pv-block-tag">Section · 02</p>
                <h2>Information we collect</h2>
              </header>
              <div className="pv-prose">
                <p>
                  We collect information you provide directly when you fill out our contact
                  form. This includes your name, email address, phone number (if provided),
                  preferred contact method, project type, and any message you send us.
                </p>
                <p>
                  We do not collect information automatically through cookies, tracking
                  pixels, or analytics tools on this site.
                </p>
              </div>
              <aside className="pv-callout" data-tone="sage">
                <span className="pv-callout-mark">No&nbsp;tracking</span>
                <p>
                  We do not collect information automatically through cookies,
                  tracking pixels, or analytics tools on this site.
                </p>
              </aside>
            </article>

            {/* 03 */}
            <article
              id="how-we-use"
              ref={registerRef('how-we-use')}
              className="pv-block"
            >
              <span className="pv-block-num" aria-hidden="true">03</span>
              <header className="pv-block-head">
                <p className="pv-block-tag">Section · 03</p>
                <h2>How we use your information</h2>
              </header>
              <div className="pv-prose">
                <p>We use the information you submit to:</p>
                <ul className="pv-list">
                  <li>Respond to your inquiry and follow up about your project</li>
                  <li>Provide you with a quote or project proposal</li>
                  <li>Communicate with you throughout a project if we work together</li>
                </ul>
                <p>
                  We do not sell, rent, or share your personal information with third
                  parties for marketing purposes.
                </p>
              </div>
              <aside className="pv-callout" data-tone="teal">
                <span className="pv-callout-mark">Won&rsquo;t&nbsp;sell</span>
                <p>
                  We do not sell, rent, or share your personal information with third
                  parties for marketing purposes.
                </p>
              </aside>
            </article>

            {/* 04 */}
            <article
              id="third-party-services"
              ref={registerRef('third-party-services')}
              className="pv-block"
            >
              <span className="pv-block-num" aria-hidden="true">04</span>
              <header className="pv-block-head">
                <p className="pv-block-tag">Section · 04</p>
                <h2>Third-party services</h2>
              </header>
              <div className="pv-prose">
                <p>
                  Our contact form is processed by{' '}
                  <a href="https://formspree.io" className="pv-link" target="_blank" rel="noreferrer">Formspree</a>,
                  a third-party form handling service. When you submit a form, your data
                  is transmitted to and stored by Formspree in accordance with their{' '}
                  <a href="https://formspree.io/legal/privacy-policy" className="pv-link" target="_blank" rel="noreferrer">
                    privacy policy
                  </a>.
                </p>
                <p>
                  Invoices and payments are handled via PayPal. Payment information is
                  processed by PayPal and subject to their{' '}
                  <a href="https://www.paypal.com/us/legalhub/privacy-full" className="pv-link" target="_blank" rel="noreferrer">
                    privacy policy
                  </a>.
                  We do not store your payment details.
                </p>
              </div>

              <div className="pv-vendors">
                <div className="pv-vendor">
                  <span className="pv-vendor-tag">Forms</span>
                  <p className="pv-vendor-name">Formspree</p>
                  <p className="pv-vendor-purpose">Receives &amp; stores contact form submissions</p>
                  <a className="pv-vendor-link" href="https://formspree.io/legal/privacy-policy" target="_blank" rel="noreferrer">
                    Privacy policy ↗
                  </a>
                </div>
                <div className="pv-vendor">
                  <span className="pv-vendor-tag">Payments</span>
                  <p className="pv-vendor-name">PayPal</p>
                  <p className="pv-vendor-purpose">Processes invoices &amp; payments</p>
                  <a className="pv-vendor-link" href="https://www.paypal.com/us/legalhub/privacy-full" target="_blank" rel="noreferrer">
                    Privacy policy ↗
                  </a>
                </div>
              </div>
            </article>

            {/* 05 */}
            <article
              id="data-retention"
              ref={registerRef('data-retention')}
              className="pv-block"
            >
              <span className="pv-block-num" aria-hidden="true">05</span>
              <header className="pv-block-head">
                <p className="pv-block-tag">Section · 05</p>
                <h2>Data retention</h2>
              </header>
              <div className="pv-prose">
                <p>
                  We retain your contact information for as long as necessary to respond
                  to your inquiry and fulfill any project-related communications. If you
                  would like your information removed, reach out through the{' '}
                  <Link to="/contact" className="pv-link">contact form</Link>{' '}
                  and we will delete it promptly.
                </p>
              </div>
            </article>

            {/* 06 */}
            <article
              id="your-rights"
              ref={registerRef('your-rights')}
              className="pv-block"
            >
              <span className="pv-block-num" aria-hidden="true">06</span>
              <header className="pv-block-head">
                <p className="pv-block-tag">Section · 06</p>
                <h2>Your rights</h2>
              </header>
              <div className="pv-prose">
                <p>You have the right to:</p>
                <ul className="pv-list pv-list-rights">
                  <li>
                    <span className="pv-list-num">i</span>
                    <span>Request a copy of the personal information we hold about you</span>
                  </li>
                  <li>
                    <span className="pv-list-num">ii</span>
                    <span>Request correction of inaccurate information</span>
                  </li>
                  <li>
                    <span className="pv-list-num">iii</span>
                    <span>Request deletion of your personal information</span>
                  </li>
                  <li>
                    <span className="pv-list-num">iv</span>
                    <span>Withdraw consent for us to contact you at any time</span>
                  </li>
                </ul>
                <p>
                  To exercise any of these rights, reach out through the{' '}
                  <Link to="/contact" className="pv-link">contact form</Link>.
                </p>
              </div>
            </article>

            {/* 07 */}
            <article
              id="changes"
              ref={registerRef('changes')}
              className="pv-block"
            >
              <span className="pv-block-num" aria-hidden="true">07</span>
              <header className="pv-block-head">
                <p className="pv-block-tag">Section · 07</p>
                <h2>Changes to this policy</h2>
              </header>
              <div className="pv-prose">
                <p>
                  We may update this policy from time to time. The date at the top of
                  this page reflects when it was last revised. Continued use of this site
                  after changes are posted constitutes acceptance of the updated policy.
                </p>
              </div>
              <div className="pv-endmark" aria-hidden="true">
                <span />
                <em>End of document</em>
                <span />
              </div>
            </article>

          </div>
        </div>
      </section>

      {/* -------- CTA -------- */}
      <section className="pv-cta">
        <div className="pv-cta-grid" aria-hidden="true" />
        <div className="pv-cta-glow" aria-hidden="true" />
        <div className="pv-cta-orbit" aria-hidden="true">
          <span /><span /><span />
        </div>
        <div className="pv-cta-rule" aria-hidden="true">
          <span>Layer &amp; Co.</span>
          <span>Privacy · v. {LAST_UPDATED}</span>
          <span>Brooklyn, NY</span>
        </div>
        <div className="pv-container pv-container-narrow pv-cta-inner">
          <p className="pv-cta-eyebrow">Any questions?</p>
          <h2>Questions about <em>this policy?</em></h2>
          <p>
            We&rsquo;d rather you ask than wonder. Send us a note. We read everything
            and reply within a business day.
          </p>
          <div className="pv-cta-actions">
            <Link to="/contact" className="pv-btn pv-btn-primary">
              Get in touch
            </Link>
          </div>
        </div>
      </section>

    </main>
  );
}

export default Privacy;